import { chakra, Flex, Spinner, Text } from "@chakra-ui/react"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type OnChangeFn,
} from "@tanstack/react-table"

type DataTableProps<T> = {
  columns: ColumnDef<T>[]
  data: T[]
  isLoading?: boolean
  emptyMessage?: string
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  globalFilter?: string
  onGlobalFilterChange?: (filter: string) => void
  manualSorting?: boolean
  onRowClick?: (row: T) => void
}

export default function DataTable<T>({
  columns,
  data,
  isLoading,
  emptyMessage,
  sorting = [],
  onSortingChange,
  globalFilter = "",
  onGlobalFilterChange,
  manualSorting = false,
  onRowClick,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange,
    onGlobalFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualSorting,
  })

  return (
    <chakra.table width="100%">
      <chakra.thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <chakra.tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <chakra.th
                key={header.id}
                fontSize="xs"
                letterSpacing="wide"
                textTransform="uppercase"
                px="3"
                py="2"
                borderColor="gray.700"
                borderBottomWidth="1px"
                cursor={header.column.getCanSort() ? "pointer" : "default"}
                onClick={header.column.getToggleSortingHandler()}
              >
                <Flex align="center" gap="2">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getIsSorted() === "asc" && <Text>▲</Text>}
                  {header.column.getIsSorted() === "desc" && <Text>▼</Text>}
                </Flex>
              </chakra.th>
            ))}
          </chakra.tr>
        ))}
      </chakra.thead>
      <chakra.tbody>
        {isLoading ? (
          <chakra.tr>
            <chakra.td colSpan={columns.length}>
              <Flex align="center" justify="center" py="6">
                <Spinner size="sm" mr="2" />
                <Text fontSize="sm">Loading…</Text>
              </Flex>
            </chakra.td>
          </chakra.tr>
        ) : table.getRowModel().rows.length === 0 ? (
          <chakra.tr>
            <chakra.td colSpan={columns.length}>
              <Text textAlign="center" fontSize="sm" color="gray.500" py="4">
                {emptyMessage ?? "No entries available."}
              </Text>
            </chakra.td>
          </chakra.tr>
        ) : (
          table.getRowModel().rows.map((row) => (
            <chakra.tr 
              key={row.id}
              cursor={onRowClick ? "pointer" : "default"}
              _hover={onRowClick ? { bg: "gray.700" } : undefined}
              onClick={() => onRowClick?.(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <chakra.td key={cell.id} px="3" py="2" borderColor="gray.700" borderBottomWidth="1px" fontSize="sm">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </chakra.td>
              ))}
            </chakra.tr>
          ))
        )}
      </chakra.tbody>
    </chakra.table>
  )
}
